import { Link, useParams } from 'react-router';

import isEmpty from 'lodash/fp/isEmpty';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import { MetaData } from 'Components/MetaData';
import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';
import HowWorks from 'Blocks/HowWorks';

import ErrorContainer from 'Containers/ErrorContainer';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';
import LoadingScene from 'Scenes/LoadingScene';

import { ingredientsMetadata } from 'assets/metadata/ingredientsMetadata';

import { ingredientURL } from 'utils/ingredients';
import { getProductPageUrl } from 'utils/productUtils';
import useGetApi from 'utils/useGetApi';

import { EwgInfos } from './EwgInfos';
import { IngredientInfos } from './IngredientInfos';

const dataFrom = 'ingredients-details';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${legacyTheme.spacing.s144};

  ${legacyTheme.breakpoints.up('sm')} {
    flex-direction: row;
  }
`;

const RightBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    width: 50%;
  }

  & img {
    display: block;
    max-width: 281px;
    height: auto;
    margin: auto;

    ${legacyTheme.breakpoints.up('sm')} {
      margin: 0;
    }
  }

  & h2 {
    width: 100%;
    margin-bottom: 8px;
    text-transform: capitalize;
    word-break: break-all;
  }
`;

const IngredientInfosWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-flow: row wrap;

  & p {
    display: flex;
    flex-flow: row wrap;
  }

  & button {
    cursor: pointer;

    padding: 0;

    color: inherit;
    text-decoration: underline;
    text-transform: uppercase;

    appearance: none;
    background: none;
    border: none;
  }
`;

const LeftBlock = styled.div`
  ${{ ...legacyTheme.typography.p2 }};

  width: 100%;

  color: ${theme.colors.neutral[800]};

  & h2 {
    ${{ ...legacyTheme.typography.mono2 }};
    color: ${theme.colors.neutral[900]};
  }

  & h3 {
    ${{ ...legacyTheme.typography.h3 }};
    padding: 10px;
    color: ${theme.colors.primary[400]};
    text-align: center;

    &::before {
      content: open-quote;
    }

    &::after {
      content: close-quote;
    }
  }

  & img {
    max-width: 281px;
  }

  & strong {
    ${{ ...legacyTheme.typography.mono2 }};
    display: block;
    padding: 14px 0 0;
    color: ${theme.colors.primary[400]};
    text-transform: uppercase;
  }

  & a {
    color: ${theme.colors.neutral[800]};
    word-wrap: break-word;
  }

  & ol {
    padding: 0 0 0 20px;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    width: 50%;
  }
`;

const Disclaimer = styled.div`
  margin-top: 72px;

  & p {
    color: ${theme.colors.neutral[800]};
  }
`;

const PreviousAndNextLinksWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-top: ${legacyTheme.spacing.s32};

  & a {
    ${{ ...legacyTheme.typography.mono3 }};
    display: block;

    width: 100%;

    line-height: 50px;
    color: ${theme.colors.neutral[800]};
    text-align: center;
    text-transform: uppercase;
  }
`;

const PreviousLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50%;

  border: 1px solid ${theme.colors.neutral[600]};
  border-left: 0;
`;

const NextLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50%;

  border: 1px solid ${theme.colors.neutral[600]};
  border-right: 0;
  border-left: 0;
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 155px;
  padding-left: 0;
  padding-top: 8px;
  padding-bottom: 8px;

  border-bottom: 1px solid ${theme.colors.neutral[600]};

  cursor: pointer;

  ${legacyTheme.breakpoints.up('sm')} {
    flex-direction: row;
    height: 167px;
  }

  & div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  & img {
    flex-direction: column;
    width: ${legacyTheme.spacing.s80};
    height: auto;
    margin-right: 0;

    ${legacyTheme.breakpoints.up('sm')} {
      flex-direction: row;
      width: ${legacyTheme.spacing.s96};
      margin-right: ${legacyTheme.spacing.s72};
    }
  }
`;

const Benefit = styled(Typography)`
  color: ${theme.colors.neutral[800]};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${legacyTheme.spacing.s36};
  margin-bottom: ${legacyTheme.spacing.s80};

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: ${legacyTheme.spacing.s144};
  }
`;

const Name = styled(Typography)`
  overflow: hidden;

  width: 110px;

  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 100%;
  }
`;

type IngredientDetail = {
  alternate_name?: string;
  article: string;
  benefits: string[];
  description: string;
  ewg_rating?: number;
  has_image: boolean;
  image_slug?: string; // @Tiago: theoretically always sent by the backend but failing on the e2e
  inci_name: string;
  locations: [];
  name: string;
  next: {
    benefits: string[];
    has_image: boolean;
    image_slug?: string; // @Tiago: theoretically always sent by the backend but failing on the e2e
    name: string;
    slug: string;
  };
  present_in: {
    label: string;
    slug: string;
  }[];
  prev: {
    benefits: string[];
    has_image: boolean;
    image_slug?: string; // @Tiago: theoretically always sent by the backend but failing on the e2e
    name: string;
    slug: string;
  };
  pubkey: string;
  slug: string;
};

const Ingredient = () => {
  const { slug = '' } = useParams();
  // @ts-expect-error -(@pierre)  TODO: use a server-side method when migrating to Next.js
  const [{ data, error, isLoading }, callApi] = useGetApi(`/v1/raw_ingredients/${slug}`);
  const content = data as IngredientDetail;

  if (error) {
    return (
      <ErrorContainer
        error={error}
        retry={callApi}
        sentryMessage="Unhandled API error on ingredient page"
      />
    );
  }

  if (isLoading) {
    // @ts-expect-error - LoadingScene has no TS types yet
    return <LoadingScene />;
  }

  const image = ingredientURL(content.image_slug || slug);

  const returnImg = (imageSlug: string) => {
    const img = ingredientURL(imageSlug);
    return img.src;
  };

  const defaultMetadata = {
    description: `${content?.name || 'Ingredient'} is known for it's ${
      content?.benefits?.[0]?.toLowerCase() ?? 'benefits'
    } ${
      content?.benefits?.[1]?.toLowerCase() ?? ''
    } in hair. Look for custom hair products to ensure you're getting exactly what your hair needs.`,
    title: `Best Ingredients for Hair: ${content?.name || 'Ingredient'} | Prose`,
  };

  const prepareMetadataIngredient = (ingredientSlug: string) => {
    if (ingredientSlug in ingredientsMetadata) {
      return ingredientsMetadata[ingredientSlug as keyof typeof ingredientsMetadata];
    }
    return defaultMetadata;
  };

  return (
    <>
      {slug && <MetaData {...prepareMetadataIngredient(slug)} />}

      <BaseScene footer FooterProps={{ hideCTA: true }} header>
        {/* @ts-expect-error - BlockLayout has no TS types yet */}
        <BlockLayout contentWidth="veryWide">
          <Root>
            <RightBlock>
              <Image alt={content.name} height={510} src={image.src} width={410} />

              <Typography variant="h2">{content.name}</Typography>

              <IngredientInfosWrapper>
                <IngredientInfos>
                  <button
                    aria-label="Scroll to EW Score"
                    data-click="view-ewg-rating"
                    data-from={dataFrom}
                    onClick={() =>
                      document
                        ?.getElementById('ewg_explaination')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                    type="button"
                  >
                    ewg score:{' '}
                    {content.ewg_rating && content.ewg_rating > 0 ? content.ewg_rating : 'N/A'}
                  </button>
                </IngredientInfos>

                {content.alternate_name ? (
                  <IngredientInfos dash>{content.alternate_name}</IngredientInfos>
                ) : null}

                {content?.locations?.length > 0 &&
                  content.locations.map((location, key) => {
                    return key === 0 ? (
                      <IngredientInfos key={location} dash>
                        {location}
                      </IngredientInfos>
                    ) : (
                      <IngredientInfos key={location}>, {location}</IngredientInfos>
                    );
                  })}
              </IngredientInfosWrapper>

              <br />

              {content?.benefits?.length > 0 && (
                <IngredientInfosWrapper>
                  <Typography variant="p2">
                    <strong>Known for:&nbsp;</strong>

                    {content.benefits.map((benefit, key) => {
                      return (
                        <Typography key={benefit}>
                          {benefit}
                          {key < content.benefits.length - 1 && <>,&nbsp;</>}
                        </Typography>
                      );
                    })}
                  </Typography>
                </IngredientInfosWrapper>
              )}

              {!isEmpty(content.present_in) && (
                <IngredientInfosWrapper>
                  <Typography variant="p2">
                    <strong>Can be found in:&nbsp;</strong>

                    {content.present_in.map((product, key) => {
                      return key === 0 ? (
                        <Typography key={product.label}>
                          <a href={getProductPageUrl(product.slug)}>{product.label}</a>
                        </Typography>
                      ) : (
                        <Typography key={product.label}>
                          , <a href={getProductPageUrl(product.slug)}>{product.label}</a>
                        </Typography>
                      );
                    })}
                  </Typography>
                </IngredientInfosWrapper>
              )}
            </RightBlock>

            <LeftBlock>
              {/* TODO: replace this code by normal HTML */}
              <div
                dangerouslySetInnerHTML={{
                  __html: content.article // Article might be emptyString
                    ? content.article
                    : content.description,
                }}
              />

              <Disclaimer>
                <Typography variant="p4">
                  This information is provided for educational purposes only and is not intended to
                  be used to treat any health conditions. Medical advice should always come from
                  your physician. The articles mentioned are for reference only and are unaffiliated
                  with Prose.
                </Typography>
              </Disclaimer>
            </LeftBlock>
          </Root>

          <section>
            <Typography align="center" paragraph variant="mono2">
              discover more ingredients
            </Typography>

            <PreviousAndNextLinksWrapper>
              <PreviousLink
                data-click="view-ingredient-prev"
                data-from={dataFrom}
                to={`/ingredients/${content.prev.slug}`}
              >
                <ImageWrapper>
                  <Image
                    alt={content.prev.slug}
                    height={510}
                    src={returnImg(content.prev.image_slug || content.prev.slug)}
                    width={410}
                  />
                  <div>
                    <Name variant="p2">{content.prev.name}</Name>

                    <Benefit variant="p3">{content.prev.benefits[0]}</Benefit>
                  </div>
                </ImageWrapper>
                previous
              </PreviousLink>

              <NextLink
                data-click="view-ingredient-next"
                data-from={dataFrom}
                to={`/ingredients/${content.next.slug}`}
              >
                <ImageWrapper>
                  <Image
                    alt={content.next.slug}
                    height={510}
                    src={returnImg(content.next.image_slug || content.next.slug)}
                    width={410}
                  />
                  <div>
                    <Name variant="p2">{content.next.name}</Name>

                    <Benefit variant="p3">{content.next.benefits[0]}</Benefit>
                  </div>
                </ImageWrapper>
                next
              </NextLink>
            </PreviousAndNextLinksWrapper>

            <ButtonWrapper id="ewg_explaination">
              <Button
                Component={Link}
                data-click="view-full-list"
                data-from={dataFrom}
                to="/ingredients"
                variant="vert"
              >
                explore our full list
              </Button>
            </ButtonWrapper>
          </section>
          <EwgInfos />
        </BlockLayout>

        <HowWorks cta dataFrom={dataFrom} />
      </BaseScene>
    </>
  );
};

export default Ingredient;
